/** Official FDA M1 2.6 Tables 2–4 and FDA controlled vocabularies, verified 2026-10-25.
 * https://www.fda.gov/media/159382/download?attachment=
 * https://www.accessdata.fda.gov/static/eCTD/submission-type.xml
 * https://www.accessdata.fda.gov/static/eCTD/submission-sub-type.xml
 */
export const FDA_SUBMISSION_TYPES = {
  fdast1: 'Original Application', fdast2: 'Efficacy Supplement',
  fdast3: 'Chemistry Manufacturing Controls Supplement', fdast4: 'Labeling Supplement',
  fdast5: 'Annual Report', fdast6: 'Product Correspondence',
  fdast7: 'Postmarketing Requirements or Postmarketing Commitments',
  fdast8: 'Promotional Labeling Advertising', fdast9: 'IND Safety Reports',
  fdast10: 'Periodic Safety Reports', fdast11: 'REMS Supplement',
} as const;
export const FDA_SUBMISSION_SUBTYPES = {
  fdasst1: 'Original', fdasst2: 'Presubmission', fdasst3: 'Application',
  fdasst4: 'Amendment', fdasst5: 'Resubmission', fdasst6: 'Report', fdasst7: 'Correspondence',
} as const;
export const FDA_SUPPLEMENT_EFFECTIVE_DATES = {
  fdasedt1: 'Prior Approval Supplement (PAS)',
  fdasedt2: 'Changes Being Effected-0 (CBE-0)',
  fdasedt3: 'Changes Being Effected-30 (CBE-30)',
} as const;
type Activity = keyof typeof FDA_SUBMISSION_TYPES;
type Subtype = keyof typeof FDA_SUBMISSION_SUBTYPES;
const ACTIVITY_RULES: Readonly<Record<Activity, { applications: readonly string[]; subtypes: readonly Subtype[]; effectiveDates?: readonly string[] }>> = {
  fdast1: { applications: ['IND', 'NDA', 'ANDA', 'BLA', 'DMF', 'EUA'], subtypes: ['fdasst2', 'fdasst3', 'fdasst4', 'fdasst5'] },
  fdast2: { applications: ['NDA', 'BLA'], subtypes: ['fdasst2', 'fdasst3', 'fdasst4', 'fdasst5'], effectiveDates: ['fdasedt1'] },
  fdast3: { applications: ['NDA', 'ANDA', 'BLA'], subtypes: ['fdasst2', 'fdasst3', 'fdasst4', 'fdasst5'], effectiveDates: ['fdasedt1', 'fdasedt2', 'fdasedt3'] },
  fdast4: { applications: ['NDA', 'ANDA', 'BLA'], subtypes: ['fdasst2', 'fdasst3', 'fdasst4', 'fdasst5'], effectiveDates: ['fdasedt1', 'fdasedt2'] },
  fdast5: { applications: ['IND', 'NDA', 'ANDA', 'BLA', 'DMF'], subtypes: ['fdasst6', 'fdasst4'] },
  fdast6: { applications: ['IND', 'NDA', 'ANDA', 'BLA', 'DMF'], subtypes: ['fdasst7', 'fdasst4'] },
  fdast7: { applications: ['NDA', 'BLA'], subtypes: ['fdasst1', 'fdasst4'] },
  fdast8: { applications: ['NDA', 'ANDA', 'BLA', 'EUA'], subtypes: ['fdasst1', 'fdasst5', 'fdasst4'] },
  fdast9: { applications: ['IND'], subtypes: ['fdasst6', 'fdasst4'] },
  fdast10: { applications: ['NDA', 'ANDA', 'BLA'], subtypes: ['fdasst6', 'fdasst4'] },
  fdast11: { applications: ['NDA', 'ANDA', 'BLA'], subtypes: ['fdasst3', 'fdasst4', 'fdasst5'], effectiveDates: ['fdasedt1', 'fdasedt3'] },
};

export function isValidFdaActivitySelection(application: string, activity: string, subtype: string): boolean {
  if (!Object.hasOwn(ACTIVITY_RULES, activity)) return false;
  const rule = ACTIVITY_RULES[activity as Activity];
  return !!rule && rule.applications.includes(application) && rule.subtypes.includes(subtype as Subtype);
}

export function fdaSupplementEffectiveDateOptions(activity: string, subtype: string): readonly string[] {
  return subtype === 'fdasst3' ? ACTIVITY_RULES[activity as Activity]?.effectiveDates ?? [] : [];
}

/** Canonical supplement-category check shared by creation, review and compilation. */
export function fdaSupplementEffectiveDateError(activity: string, subtype: string, effective: string | null | undefined): string | null {
  const options = fdaSupplementEffectiveDateOptions(activity, subtype);
  if (options.length > 0 && !effective) return 'Select the supplement effective date type for this FDA application submission.';
  if (effective && !options.includes(effective)) return 'The supplement effective date type is not permitted for this FDA activity and subtype; correct or remove it.';
  return null;
}

/** Shared catalog snapshot builder; effective-date choices are separate from activity/subtype identity. */
export function officialFdaSubmissionCatalogRows() {
  return Object.entries(ACTIVITY_RULES).flatMap(([code, rule], activityIndex) =>
    rule.applications.flatMap((application) => rule.subtypes.map((subtype, subtypeIndex) => ({
      Region: 'FDA', ApplicationType: application,
      SubmissionTypeCode: code, SubmissionTypeName: FDA_SUBMISSION_TYPES[code as Activity],
      SubmissionSubTypeCode: subtype, SubmissionSubTypeName: FDA_SUBMISSION_SUBTYPES[subtype],
      SupplementEffectiveDateType: null,
      Description: 'FDA Module 1 specification 2.6, Table 2. Select the activity and subtype that describe this filing.',
      RequiresPriorDocumentSet: code !== 'fdast1' || subtype === 'fdasst4' || subtype === 'fdasst5',
      ValidSubTypes: JSON.stringify(rule.subtypes), SourceUrl: 'https://www.fda.gov/media/159382/download?attachment=',
      SortOrder: (activityIndex + 1) * 10 + subtypeIndex, IsActive: true,
    }))),
  );
}
